import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { formatDate } from './DateDisplay';

const formatNumbersToDisplay = (numbers) => {
  if (!numbers) return '';

  const sanitized = numbers.slice(0, 8);

  if (sanitized.length <= 2) return sanitized;
  if (sanitized.length <= 4) {
    return `${sanitized.slice(0, 2)}/${sanitized.slice(2, 4)}`;
  }

  return `${sanitized.slice(0, 2)}/${sanitized.slice(2, 4)}/${sanitized.slice(4, 8)}`;
};

const isValidDate = (yearStr, monthStr, dayStr) => {
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (!year || !month || !day) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const numbersToIso = (numbers) => {
  if (numbers.length !== 8) return null;

  const day = numbers.slice(0, 2);
  const month = numbers.slice(2, 4);
  const year = numbers.slice(4, 8);

  if (!isValidDate(year, month, day)) return null;

  return `${year}-${month}-${day}`;
};

const isoToDisplay = (value) => formatDate(value) ?? '';

const emitChange = (onChange, name, value) => {
  if (typeof onChange !== 'function') return;
  onChange({ target: { name, value } });
};

export default function DateInput(props) {
  const {
    value,
    name,
    onChange,
    onBlur,
    placeholder = 'dd/MM/yyyy',
    readOnly = false,
    className = 'form-control form-control-sm',
    min,
    max,
    wrapperClassName = '',
    wrapperStyle,
    iconClassName = 'fa-solid fa-calendar-days',
    iconAriaLabel = 'Abrir calendário',
    ...rest
  } = props;

  const { style: inputStyle, disabled, ...inputProps } = rest;

  const [displayValue, setDisplayValue] = useState(() => isoToDisplay(value));
  const hiddenDateInputRef = useRef(null);

  useEffect(() => {
    setDisplayValue(isoToDisplay(value));
  }, [value]);

  const handleTextChange = (event) => {
    const raw = event.target.value ?? '';
    const numbers = raw.replace(/\D/g, '').slice(0, 8);
    const formatted = formatNumbersToDisplay(numbers);

    setDisplayValue(formatted);

    if (numbers.length === 0) {
      emitChange(onChange, name, '');
      return;
    }

    if (numbers.length === 8) {
      const iso = numbersToIso(numbers);
      emitChange(onChange, name, iso ?? '');
    }
  };

  const handleTextBlur = (event) => {
    const numbers = displayValue.replace(/\D/g, '');

    if (numbers.length === 0) {
      emitChange(onChange, name, '');
      setDisplayValue('');
    } else if (numbers.length === 8) {
      const iso = numbersToIso(numbers);
      if (iso) {
        setDisplayValue(isoToDisplay(iso));
        emitChange(onChange, name, iso);
      } else {
        emitChange(onChange, name, '');
        setDisplayValue('');
      }
    } else {
      emitChange(onChange, name, '');
      setDisplayValue('');
    }

    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  };

  const handleCalendarChange = (event) => {
    const iso = event.target.value;

    if (!iso) {
      setDisplayValue('');
      emitChange(onChange, name, '');
      return;
    }

    setDisplayValue(isoToDisplay(iso));
    emitChange(onChange, name, iso);
  };

  const openCalendar = () => {
    if (readOnly || disabled) return;
    if (!hiddenDateInputRef.current) return;

    if (typeof hiddenDateInputRef.current.showPicker === 'function') {
      hiddenDateInputRef.current.showPicker();
    } else {
      hiddenDateInputRef.current.focus();
      hiddenDateInputRef.current.click();
    }
  };

  const wrapperStyles = {
    position: 'relative',
    width: '100%',
    ...(wrapperStyle || {}),
  };

  return (
    <div className={wrapperClassName} style={wrapperStyles}>
      <input
        type="text"
        name={name}
        className={className}
        value={displayValue}
        placeholder={placeholder}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        readOnly={readOnly}
        disabled={disabled}
        style={{
          width: '100%',
          marginLeft: 0,
          paddingRight: '2.5rem',
          ...(inputStyle || {}),
        }}
        {...inputProps}
      />
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={openCalendar}
        aria-label={iconAriaLabel}
        disabled={disabled || readOnly}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 0.75rem',
          border: 'none',
          background: 'transparent',
          color: 'inherit',
          cursor: disabled || readOnly ? 'not-allowed' : 'pointer',
        }}
      >
        <i className={iconClassName} aria-hidden="true"></i>
      </button>
      <input
        type="date"
        ref={hiddenDateInputRef}
        value={value ?? ''}
        min={min}
        max={max}
        onChange={handleCalendarChange}
        readOnly={readOnly}
        disabled={disabled}
        tabIndex={-1}
        style={{
          position: 'absolute',
          opacity: 0,
          pointerEvents: 'none',
          width: 0,
          height: 0,
        }}
      />
    </div>
  );
}

DateInput.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  wrapperClassName: PropTypes.string,
  wrapperStyle: PropTypes.object,
  iconClassName: PropTypes.string,
  iconAriaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  style: PropTypes.object,
};

